import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DescriptionIcon from "@mui/icons-material/Description";
import CampaignIcon from "@mui/icons-material/Campaign";
import FlagIcon from "@mui/icons-material/Flag";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BlockIcon from "@mui/icons-material/Block";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { useGetResellerRcsCampaignDetailsQuery } from "../store/services/rcstemplateApi";
import { layoutCSS } from "../component/common/commonStyle";

// ── Styles ────────────────────────────────────────────────────────────────────

// Every component uses this sharp, rectangular corner radius
const RECT = "2px";

// Only the table header is bold
const headerCellSX = {
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#fff",
  textAlign: "center",
  padding: "10px 8px",
  borderBottom: "none",
  whiteSpace: "nowrap",
};

// Table body: normal weight
const bodyCellSX = {
  fontSize: "0.75rem",
  fontWeight: 400,
  padding: "8px",
  textAlign: "center",
  borderBottom: "1px solid #E3EEF8",
  color: "#1E293B",
};

const sectionCardSX = {
  borderRadius: RECT,
  overflow: "hidden",
  height: "100%",
  border: "1px solid #E3EEF8",
  boxShadow: "0 6px 18px rgba(2,136,209,0.10)",
  background: "rgba(255,255,255,0.75)",
  backdropFilter: "blur(10px)",
};

const sectionHeaderSX = (from, to) => ({
  px: 2,
  py: 1,
  color: "#fff",
  background: `linear-gradient(90deg, ${from}, ${to})`,
  display: "flex",
  alignItems: "center",
  gap: 1,
});

// Colour themes for the three statistic groups
const STAT_THEMES = {
  Submission: { bg: "#F2F7FB", fg: "#5B86A8", border: "#D5E3EF" },
  Delivery: { bg: "#F2F8F3", fg: "#628F6A", border: "#D6E6D9" },
  Failed: { bg: "#FBF4F3", fg: "#B77A74", border: "#EDD9D6" },
};

const STAT_ICONS = {
  Total: FormatListNumberedIcon,
  Submit: SendIcon,
  Sent: SendIcon,
  Pending: HourglassEmptyIcon,
  Delivered: DoneAllIcon,
  Read: VisibilityIcon,
  Click: TouchAppIcon,
  Disabled: BlockIcon,
  "Limit Exhausted": ReportProblemIcon,
  "TTL Expired": TimerOffIcon,
  Failed: ErrorOutlineIcon,
  Rejected: BlockIcon,
  Other: ErrorOutlineIcon,
};

const INFO_ITEMS = [
  { label: "Name", key: "campaign_name", icon: CampaignIcon, color: "#0288d1", bg: "#E1F5FE" },
  { label: "Bot", key: "bot_name", icon: SmartToyIcon, color: "#7b1fa2", bg: "#F3E5F5" },
  { label: "Template", key: "template_name", icon: DescriptionIcon, color: "#ef6c00", bg: "#FFF3E0" },
  { label: "Status", key: "status", icon: FlagIcon, color: "#2e7d32", bg: "#E8F5E9" },
];

// ─────────────────────────────────────────────────────────────────────────────

const ResellerRcsCampaignDetailsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const {
    data: details,
    isLoading,
    isFetching,
    isError,
  } = useGetResellerRcsCampaignDetailsQuery(
    { campaignId, page, pagesize: pageSize },
    { skip: !campaignId },
  );

  const campaign = details?.campaign;
  const overview = details?.overview;
  const rows = details?.data || [];
  const pagination = details?.pagination;

  const getStatusColor = (status) => {
    if (!status) return "default";
    const value = status.toLowerCase();
    if (value.includes("delivered") || value.includes("read")) return "success";
    if (value.includes("failed") || value.includes("fail")) return "error";
    if (value.includes("sent")) return "info";
    return "default";
  };

  const overviewSections = [
    {
      title: "Submission",
      values: [
        ["Total", overview?.total],
        ["Submit", overview?.submit],
        ["Sent", overview?.sent],
        ["Pending", overview?.pending],
      ],
    },
    {
      title: "Delivery",
      values: [
        ["Delivered", overview?.delivered],
        ["Read", overview?.read],
        ["Click", overview?.click],
      ],
    },
    {
      title: "Failed",
      values: [
        ["Disabled", overview?.disabled],
        ["Limit Exhausted", overview?.limitExhausted],
        ["TTL Expired", overview?.ttlExpired],
        ["Failed", overview?.failed],
        ["Rejected", overview?.rejected],
        ["Other", overview?.otherFailed],
      ],
    },
  ];

  const loading = isLoading || isFetching;

  return (
    <Box sx={{ ...layoutCSS, py: 1 }}>
      {/* ── Hero header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
          px: 2,
          py: 1.5,
          borderRadius: RECT,
          color: "#fff",
          background: "linear-gradient(120deg, #0288d1 0%, #5e35b1 100%)",
          boxShadow: "0 8px 20px rgba(94,53,177,0.25)",
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: "0.08em" }}>
            RCS CAMPAIGN DETAILS
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.2 }}>
            {campaign?.campaign_name || "Campaign"}{" "}
            <Box component="span" sx={{ opacity: 0.85, fontWeight: 500 }}>
              · ID {campaign?.campaign_id || campaignId}
            </Box>
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/deliveryreport")}
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            color: "#fff",
            textTransform: "none",
            borderRadius: RECT,
            boxShadow: "none",
            "&:hover": { bgcolor: "rgba(255,255,255,0.35)", boxShadow: "none" },
          }}
        >
          Back to report
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isError && (
        <Typography color="error" role="alert" sx={{ mb: 2 }}>
          Failed to load RCS campaign details. Please try again.
        </Typography>
      )}

      {campaign && (
        <>
          <Grid container spacing={2} mb={2}>
            {/* ── Campaign information ── */}
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={sectionCardSX}>
                <Box sx={sectionHeaderSX("#0288d1", "#26c6da")}>
                  <CampaignIcon fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Campaign Information
                  </Typography>
                </Box>
                <Grid container spacing={1.5} sx={{ p: 2 }}>
                  {INFO_ITEMS.map(({ label, key, icon: Icon, color, bg }) => {
                    const value = campaign[key];
                    return (
                      <Grid item xs={6} key={label}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            borderRadius: RECT,
                            bgcolor: bg,
                            height: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: RECT,
                              bgcolor: "#fff",
                              color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.1 }}>
                              {label}
                            </Typography>
                            {label === "Status" ? (
                              <Chip
                                label={value || "N/A"}
                                size="small"
                                color={getStatusColor(value)}
                                sx={{ height: 20, fontSize: "0.68rem", fontWeight: 600, mt: 0.3, borderRadius: RECT }}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color, wordBreak: "break-word", fontSize: "0.8rem" }}
                              >
                                {value || "N/A"}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            {/* ── Message statistics ── */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={sectionCardSX}>
                <Box sx={{ ...sectionHeaderSX("#8793b8", "#a2abc9"), py: 0.6 }}>
                  <FormatListNumberedIcon fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Message Statistics
                  </Typography>
                </Box>
                <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 0.9 }}>
                  {overviewSections.map(({ title, values }) => {
                    const items = values.filter(([, value]) => value !== undefined);
                    if (!items.length) return null;
                    const theme = STAT_THEMES[title];
                    return (
                      <Box key={title}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: theme.fg,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            fontSize: "0.62rem",
                            display: "block",
                            mb: 0.3,
                          }}
                        >
                          {title}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                          {items.map(([label, value]) => {
                            const Icon = STAT_ICONS[label] || FormatListNumberedIcon;
                            return (
                              <Box
                                key={`${title}-${label}`}
                                sx={{
                                  flex: "1 1 88px",
                                  minWidth: 88,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.7,
                                  px: 0.9,
                                  py: 0.4,
                                  borderRadius: RECT,
                                  bgcolor: theme.bg,
                                  border: `1px solid ${theme.border}`,
                                  borderLeft: `3px solid ${theme.fg}`,
                                  transition: "box-shadow .15s",
                                  "&:hover": {
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
                                  },
                                }}
                              >
                                <Icon sx={{ fontSize: 16, color: theme.fg, opacity: 0.85 }} />
                                <Box>
                                  {/* Stat label: now bolder */}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: "block",
                                      lineHeight: 1.1,
                                      fontSize: "0.64rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", color: theme.fg, lineHeight: 1.2 }}>
                                    {value ?? 0}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ── Message details header ── */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#0277BD" }}>
              Message Details
            </Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                aria-label="Rows per page"
                sx={{
                  fontSize: "0.75rem",
                  bgcolor: "rgba(255,255,255,0.8)",
                  borderRadius: RECT,
                }}
              >
                {[25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size} sx={{ fontSize: "0.75rem" }}>
                    {size} per page
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* ── Message details table ── */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: RECT,
              overflow: "hidden",
              border: "1px solid #CFE3F4",
              boxShadow: "0 6px 18px rgba(2,136,209,0.10)",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(90deg, #0288d1, #5e35b1)" }}>
                  {["Sr No", "User", "Send Time", "Done Time", "Provider", "Status", "Reason"].map(
                    (heading) => (
                      <TableCell key={heading} sx={headerCellSX}>
                        {heading}
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.sr_no ?? `${row.user}-${index}`}
                    sx={{
                      bgcolor: index % 2 === 0 ? "rgba(255,255,255,0.85)" : "#F1F8FE",
                      transition: "background-color .15s",
                      "&:hover": { bgcolor: "#DCEEFB" },
                    }}
                  >
                    <TableCell sx={{ ...bodyCellSX, color: "#0277BD" }}>
                      {row.sr_no ?? (page - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={bodyCellSX}>{row.user || "N/A"}</TableCell>
                    <TableCell sx={bodyCellSX}>
                      {row.send_time ? dayjs(row.send_time).format("DD/MM/YYYY HH:mm") : "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSX}>
                      {row.done_time ? dayjs(row.done_time).format("DD/MM/YYYY HH:mm") : "N/A"}
                    </TableCell>
                    <TableCell sx={bodyCellSX}>
                      <Chip
                        label={row.provider || "N/A"}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          fontWeight: 400,
                          borderColor: "#7e57c2",
                          color: "#5e35b1",
                          borderRadius: RECT,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSX}>
                      <Chip
                        label={row.message_status || "N/A"}
                        size="small"
                        color={getStatusColor(row.message_status)}
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 400, borderRadius: RECT }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSX, maxWidth: 300, wordBreak: "break-word" }}>
                      {row.reason || "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && !isFetching && !isError && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ ...bodyCellSX, py: 3, color: "text.secondary" }}>
                      No message details found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Footer ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Chip
              label={`Total Records: ${pagination?.totalRecords ?? 0}`}
              size="small"
              sx={{ bgcolor: "#E1F5FE", color: "#0277BD", fontWeight: 600, borderRadius: RECT }}
            />
            {pagination?.totalPages > 1 && (
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
                sx={{ "& .MuiPaginationItem-root": { borderRadius: RECT } }}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ResellerRcsCampaignDetailsPage;